// ui/src/utils/reembolsoCentral.ts
//
// Apresentação PURA da Central do financeiro. Os componentes `.vue` não são testáveis
// no Vitest do ui (environment 'node'), então toda decisão vive aqui. NÃO re-deriva
// regra de negócio: abas, `pode_pagar`, `pode_antecipar_janela` e `janela_efetiva_em`
// vêm prontos do backend (lição do `tomDoCstat`, Fiscal TSS).
import type {
  AbaCentral, AcaoProblema, Despesa, EmpresaCentral, FilaCentral, ItemCentral, PreviewCadeia,
  ResultadoPagarLote,
} from '@/services/reembolsoApi';
import { extractApiErrorMessage } from '@/utils/apiError';
import { lerMapa } from '@/utils/reembolsoMapaSeguro';
import { rotuloAcaoHistorico } from '@/utils/reembolsoHistoricoRotulo';
import { formatBRL, formatMoeda } from '@/utils/reembolsoFormat';

export const ABAS_META: Array<{ key: AbaCentral; label: string }> = [
  { key: 'conferir', label: 'Conferir' },
  { key: 'aguardando_erp', label: 'Aguardando ERP' },
  { key: 'pagar', label: 'Pagar' },
  { key: 'pagos', label: 'Pagos no mês' },
  { key: 'problema', label: 'Com problema' },
];

/** "há 20 min", "há 9h", "há 1 dia". */
export function rotuloRecebido(iso: string | null | undefined, agora: Date = new Date()): string {
  if (!iso) return '—';
  const min = Math.floor((agora.getTime() - new Date(iso).getTime()) / 60000);
  if (min < 1) return 'agora';
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h}h`;
  const d = Math.floor(h / 24);
  return `há ${d} ${d === 1 ? 'dia' : 'dias'}`;
}

/**
 * Empresa: SÓ nome (regra do dono). Resolvida sem nome ≠ não definida. Defesa na apresentação:
 * o servidor já remove o código de filial do nome; se um dia vazar, ele sai aqui também.
 */
export function rotuloEmpresa(e: Pick<EmpresaCentral, 'nome' | 'definida'>): string {
  const nome = (e.nome ?? '')
    .replace(/(?<!\d)\d{6}(?!\d)/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .replace(/^[\s\-–—·:|/]+|[\s\-–—·:|/]+$/g, '')
    .trim();
  if (nome) return nome;
  return e.definida ? 'Empresa sem nome' : 'Não definida';
}

const COR_EMPRESA: Array<[RegExp, string]> = [
  [/atlan/i, '#2f5d9e'],
  [/via/i, '#b5651d'],
  [/sim/i, '#2f7d76'],
];
export function corDaEmpresa(nome: string | null | undefined): string {
  if (!nome) return '#98a2b3';
  return COR_EMPRESA.find(([re]) => re.test(nome))?.[1] ?? '#98a2b3';
}

const CHAVE_PIX: Record<string, string> = {
  cpf: 'CPF', cnpj: 'CNPJ', email: 'e-mail', telefone: 'telefone', aleatoria: 'chave aleatória',
};
export function rotuloPagamento(p: ItemCentral['pagamento']): string {
  if (!p) return '—';
  if (p.tipo === 'ted') return 'TED';
  return p.pix_tipo_chave ? `PIX · ${lerMapa(CHAVE_PIX, p.pix_tipo_chave) ?? p.pix_tipo_chave}` : 'PIX';
}

/** (+42%) sobre o padrão; `null` quando não há limite aplicado. */
export function percentualAcimaPadrao(valor: number, limite: number | null | undefined): number | null {
  if (!limite || limite <= 0) return null;
  return Math.round((valor / limite - 1) * 100);
}

/**
 * Selo da política na conferência. `rotulo` é o subtipo (ou a categoria) — a MESMA leitura do
 * painel do lançamento ("Padrão jantar R$ 35,00 · +42%"), para o analista e o solicitante verem
 * a mesma frase sobre a mesma despesa.
 */
export function rotuloPadrao(
  d: Pick<Despesa, 'valor' | 'moeda' | 'limite_aplicado' | 'acima_padrao'>,
  rotulo?: string | null,
): { texto: string; pct: number | null; acima: boolean } | null {
  if (d.limite_aplicado == null) return null;
  const limite = Number(d.limite_aplicado);
  const qual = (rotulo ?? '').trim().toLowerCase();
  return {
    texto: `Padrão ${qual ? `${qual} ` : ''}${formatMoeda(limite, d.moeda ?? 'BRL')}`,
    pct: percentualAcimaPadrao(Number(d.valor ?? 0), limite),
    acima: !!d.acima_padrao,
  };
}

/** Anterior/Próximo entre os pedidos da lista. `atual` fora da lista (acabou de sair) → o primeiro. */
export function proximoDaLista(ids: number[], atual: number | null, passo: 1 | -1): number | null {
  if (!ids.length) return null;
  const i = atual == null ? -1 : ids.indexOf(atual);
  if (i === -1) return ids[0];
  return ids[i + passo] ?? null;
}

/**
 * Tela dividida: depois de conferir/reprovar, abre o vizinho do concluído (o seguinte; sem ele, o anterior),
 * escolhido na lista ANTES de recarregar (o concluído sai dela). Se o vizinho também sumiu, o primeiro
 * da lista nova; lista vazia → `null` (a página volta à tabela).
 */
export function pedidoAposConcluir(idsAntes: number[], concluido: number, idsDepois: number[]): number | null {
  const i = idsAntes.indexOf(concluido);
  const vizinho = i === -1 ? null : (idsAntes[i + 1] ?? idsAntes[i - 1] ?? null);
  if (vizinho != null && idsDepois.includes(vizinho)) return vizinho;
  return idsDepois[0] ?? null;
}

/** Nome do primeiro aprovador da alçada depois da conferência (mesma regra do detalhe de conferência). */
export function proximoAprovadorDaConferencia(c: PreviewCadeia | null | undefined): string | null {
  const e = c?.etapas.find((x) => x.nivel >= 1 && !x.pulado && x.aprovador);
  return e?.aprovador?.nome ?? null;
}

/**
 * "Conferir e encaminhar" só habilita com TODAS as despesas conferidas (decisão do dono, 09/10).
 * ESPELHO da regra do servidor (422 no `conferir`): a tela só desabilita o botão; quem recusa é o servidor.
 */
export function resumoConferidas(despesas: Array<Pick<Despesa, 'conferida_em'>>) {
  const total = despesas.length;
  const conferidas = despesas.filter((d) => !!d.conferida_em).length;
  const completo = conferidas === total;
  return {
    conferidas, total, completo,
    rotulo: `${conferidas} de ${total} conferidas`,
    dica: completo ? null : 'Marque todas as despesas como conferidas',
  };
}

/** Soma em centavos (0,1 + 0,2 ≠ 0,30000000000000004). */
export function resumoSelecao(itens: Array<Pick<ItemCentral, 'id' | 'total_final'>>, selecionados: Set<number>) {
  let centavos = 0;
  let qtd = 0;
  for (const i of itens) {
    if (selecionados.has(i.id)) { qtd += 1; centavos += Math.round(i.total_final * 100); }
  }
  return { qtd, total: centavos / 100 };
}

export function resultadoPagarLote(r: { resultados: ResultadoPagarLote[]; pagos: number; falhas: number }) {
  const falhas = r.resultados.filter((x) => !x.ok).map((x) => ({ id: x.id, numero: x.numero, erro: x.erro ?? 'Falha ao marcar como pago' }));
  if (r.falhas === 0) {
    return { tipo: 'sucesso' as const, titulo: r.pagos === 1 ? '1 pedido marcado como pago' : `${r.pagos} pedidos marcados como pagos`, falhas };
  }
  if (r.pagos === 0) return { tipo: 'falha' as const, titulo: 'Nenhum pedido foi marcado como pago', falhas };
  return { tipo: 'parcial' as const, titulo: `${r.pagos} pago(s) e ${r.falhas} com falha`, falhas };
}

/**
 * Ações da linha em Com problema. O servidor já filtra `problema.acoes`; aqui só se aplica
 * a mesma decisão DELE (`pode_reenviar`) como segunda trava: "Reenviar" nunca aparece para
 * quem não é admin da configuração, mesmo que um dia `acoes` e o booleano divirjam.
 */
export function acoesDoProblema(i: Pick<ItemCentral, 'pode_reenviar' | 'problema'>): AcaoProblema[] {
  return (i.problema?.acoes ?? []).filter((a) => a !== 'reenviar' || i.pode_reenviar);
}

/** Espelho do guard do servidor: código de filial (6 dígitos isolados) nunca aparece na interface. */
export function ocultarCodigoDeFilial(texto: string): string {
  return texto.replace(/(?<!\d)\d{6}(?!\d)/g, '••••••');
}

/**
 * ÚNICA saída de texto de erro da Central (toast, faixa de erro): erro da API (`data.error`/`message`/`details`)
 * ou mensagem já em texto (ex.: `protheus_erro` relido após reenviar) — sempre sem código de filial.
 */
export function mensagemDeErroDaCentral(e: unknown, fallback: string): string {
  const texto = typeof e === 'string' ? (e.trim() || fallback) : extractApiErrorMessage(e, fallback);
  return ocultarCodigoDeFilial(texto);
}

/** Texto do "Ver erro": o erro gravado (já limpo pelo servidor) ou o detalhe do problema — sempre sem código de filial. */
export function textoDoErro(i: Pick<ItemCentral, 'protheus_erro' | 'problema'>): string {
  return ocultarCodigoDeFilial(i.protheus_erro || i.problema?.detalhe || '');
}

const FMT_DIA_BRT = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' });
/** ISO → 'AAAAMMDD' em Brasília: o formato da coluna `type: 'date'` da FunnelTable. */
export function diaAAAAMMDD(iso: string | null | undefined): string {
  return iso ? FMT_DIA_BRT.format(new Date(iso)).replace(/-/g, '') : '';
}

type Celula = string | number;
export function linhasParaPlanilha(aba: AbaCentral, itens: ItemCentral[], agora: Date = new Date()) {
  const comum = (i: ItemCentral): Celula[] => [i.numero, i.solicitante.nome, i.departamento ?? '', rotuloEmpresa(i.empresa)];
  switch (aba) {
    case 'conferir':
      return {
        cabecalho: ['Pedido', 'Solicitante', 'Departamento', 'Empresa', 'Despesas', 'Acima do padrão', 'Valor', 'Recebido'],
        linhas: itens.map((i) => [...comum(i), i.n_despesas, i.n_acima_padrao, i.total_final, rotuloRecebido(i.recebido_em, agora)]),
      };
    case 'aguardando_erp':
      return {
        cabecalho: ['Pedido', 'Solicitante', 'Departamento', 'Empresa', 'Situação', 'Valor'],
        linhas: itens.map((i) => [...comum(i), i.janela_efetiva_em ? `Retido até ${i.janela_efetiva_em.split('-').reverse().join('/')}` : 'Na fila de envio', i.total_final]),
      };
    case 'pagar':
      return {
        cabecalho: ['Pedido', 'Solicitante', 'Departamento', 'Empresa', 'Documento', 'Pagamento', 'Valor'],
        linhas: itens.map((i) => [...comum(i), i.protheus_documento ?? '', rotuloPagamento(i.pagamento), i.total_final]),
      };
    case 'pagos':
      return {
        cabecalho: ['Pedido', 'Solicitante', 'Departamento', 'Empresa', 'Documento', 'Pago em', 'Valor'],
        linhas: itens.map((i) => [...comum(i), i.protheus_documento ?? '', i.pago_em ? new Date(i.pago_em).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' }) : '', i.total_final]),
      };
    default:
      return {
        cabecalho: ['Problema', 'Pedido', 'Solicitante', 'Departamento', 'Empresa', 'Detalhe', 'Valor'],
        linhas: itens.map((i) => [i.problema?.titulo ?? '', ...comum(i), i.problema?.detalhe ?? '', i.total_final]),
      };
  }
}

// ===== Pagar selecionados em blocos (a API aceita até 100 ids por chamada) =====
export const LOTE_MAXIMO_PAGAR = 100;

export function emBlocos<T>(itens: T[], tamanho: number = LOTE_MAXIMO_PAGAR): T[][] {
  if (!Number.isInteger(tamanho) || tamanho < 1) throw new RangeError(`emBlocos: tamanho inválido (${tamanho})`);
  const out: T[][] = [];
  for (let i = 0; i < itens.length; i += tamanho) out.push(itens.slice(i, i + tamanho));
  return out;
}

type RespostaLote = { resultados: ResultadoPagarLote[]; pagos: number; falhas: number };
/**
 * Envia os ids em blocos de até 100, UM bloco por vez, e consolida o resultado por id. Cada id
 * é enviado uma vez só: um bloco que falha inteiro (rede, 4xx/5xx) vira falha dos SEUS ids, e
 * os blocos seguintes não são enviados (marcados como "não enviados") — nada já confirmado é
 * repetido e nada é pago sem que a pessoa veja o resultado.
 */
export async function pagarEmBlocos(
  ids: number[],
  enviar: (lote: number[]) => Promise<RespostaLote>,
  descreverErro: (e: unknown) => string,
): Promise<RespostaLote> {
  const resultados: ResultadoPagarLote[] = [];
  const blocos = emBlocos([...new Set(ids)]);
  for (let b = 0; b < blocos.length; b += 1) {
    try {
      resultados.push(...(await enviar(blocos[b])).resultados);
    } catch (e) {
      const erro = descreverErro(e);
      resultados.push(...blocos[b].map((id) => ({ id, ok: false, erro })));
      for (const resto of blocos.slice(b + 1)) {
        resultados.push(...resto.map((id) => ({ id, ok: false, erro: 'Não enviado: o bloco anterior falhou' })));
      }
      break;
    }
  }
  const pagos = resultados.filter((r) => r.ok).length;
  return { resultados, pagos, falhas: resultados.length - pagos };
}

// ===== Exportar: TODAS as páginas da aba (a Pagar pagina em 500 no servidor) =====
export async function buscarTodasAsPaginas(
  buscar: (pagina: number) => Promise<Pick<FilaCentral, 'items' | 'pagina' | 'total_paginas'>>,
): Promise<ItemCentral[]> {
  const primeira = await buscar(1);
  const itens = [...primeira.items];
  for (let p = 2; p <= (primeira.total_paginas ?? 1); p += 1) itens.push(...(await buscar(p)).items);
  return itens;
}

// ===== Menu: quem vê a Central (decisão 4 — admin da configuração também vê) =====
/**
 * `true`/`false` decidido; `null` = ainda carregando (o menu mantém o item para não piscar).
 * Ver ≠ agir: as ações continuam decididas pelo servidor.
 */
export function podeVerCentralFinanceiro(
  isConferente: boolean | null,
  nivelConfiguracao: string | null | undefined,
  adminMaster: boolean,
): boolean | null {
  if (adminMaster || nivelConfiguracao === 'admin') return true;
  return isConferente;
}

// ===== B2: pendência da empresa e trilha compacta da cadeia =====
const TIPOS_IDENTIDADE: ReadonlySet<string> = new Set(['sem_vinculo', 'vinculo_duplo', 'sem_cgc', 'empresa_indefinida']);
/** Pendência que se resolve no Identidade (ruling 7): a tela oferece "Abrir Identidade". */
export function pendenciaAbreIdentidade(tipo: EmpresaCentral['pendencia_tipo'] | string | null | undefined): boolean {
  return !!tipo && TIPOS_IDENTIDADE.has(tipo);
}
/**
 * Texto da pendência sem o caminho cru do Identidade (vira botão) e sem código de filial.
 * Aceita as DUAS grafias: "(/apis/identidade)", gravada em `protheus_erro` nos pedidos antigos,
 * e "(/reembolso/identidade)", a rota nova (15/10/2026).
 */
export function textoPendenciaEmpresa(texto: string | null | undefined): string {
  return ocultarCodigoDeFilial(String(texto ?? '').replace(/\s*\(\/(?:apis|reembolso)\/identidade\)/g, '').trim());
}

export interface PassoTrilha { rotulo: string; estado: 'atual' | 'feito' | 'pulado' | 'futuro' }
/**
 * Trilha compacta (pontos) da cadeia na tela dividida: Solicitação · Conferência · alçadas
 * (riscada se pulada) · ERP · Pagamento — os MESMOS nomes de etapa do lançamento e dos e-mails
 * (decisão do dono: "ERP" é a etapa; "Protheus VIA" continua só como nome do sistema). A ordem e os estados seguem o ESTADO REAL do pedido
 * (`modo_conferencia`, do servidor): no modo 'antes' a Conferência é a etapa atual e as
 * alçadas são FUTURAS — nenhuma aprovação aconteceu; no 'depois' as alçadas já foram feitas
 * e a Conferência aparece UMA vez, depois delas. Sem o campo (DTO antigo/ausente) vale o
 * 'antes': nunca se mostra como concluída uma aprovação que não se sabe ter acontecido.
 * A etapa "Conferência" do próprio DTO (nível 0 ou acima do máximo) nunca entra como alçada.
 * `dataErp` (YYYY-MM-DD): a janela de envio ao ERP do pedido (canvas B2, "ERP · 23/10"); sem data
 * conhecida (seção de janelas desligada, nada gravado) a etapa é só "ERP".
 */
export function trilhaDaConferencia(cadeia: PreviewCadeia | null | undefined, dataErp?: string | null): PassoTrilha[] {
  const maximo = cadeia?.nivel_maximo ?? 0;
  const alcadas = (cadeia?.etapas ?? []).filter((e) => e.nivel >= 1 && e.nivel <= maximo);
  const depois = cadeia?.modo_conferencia === 'depois';
  const passosAlcada: PassoTrilha[] = alcadas.map((e) => ({
    rotulo: [e.nome || `Nível ${e.nivel}`, e.aprovador?.nome].filter(Boolean).join(' · '),
    estado: e.pulado ? 'pulado' : depois ? 'feito' : 'futuro',
  }));
  const conferencia: PassoTrilha = { rotulo: 'Conferência', estado: 'atual' };
  return [
    { rotulo: 'Solicitação', estado: 'feito' },
    ...(depois ? [...passosAlcada, conferencia] : [conferencia, ...passosAlcada]),
    { rotulo: dataErp && /^\d{4}-\d{2}-\d{2}/.test(dataErp) ? `ERP · ${dataErp.slice(8, 10)}/${dataErp.slice(5, 7)}` : 'ERP', estado: 'futuro' },
    { rotulo: 'Pagamento', estado: 'futuro' },
  ];
}

/**
 * Aviso de corte para a aba ABERTA, ou `null` se a carga dela coube inteira. Cada grupo tem teto próprio
 * (conferência, aprovados, pagos do mês): o texto fala só do grupo que cortou e do critério (qual ponta
 * ficou), para nunca mandar pagar o que não aparece. DTO sem `truncadas` (servidor antigo) → aviso genérico.
 */
export function avisoDeTruncamento(
  fila: { truncada?: boolean; truncadas?: Array<{ abas: AbaCentral[]; mantidos: 'mais_antigos' | 'mais_recentes' }>; teto?: number } | null | undefined,
  aba: AbaCentral,
): string | null {
  if (!fila?.truncada) return null;
  const teto = (fila.teto ?? 2000).toLocaleString('pt-BR');
  if (!fila.truncadas) {
    return `A Central atingiu o limite de ${teto} pedidos carregados: parte dos pedidos fica fora das listas e das contagens.`;
  }
  const grupo = fila.truncadas.find((t) => t.abas.includes(aba));
  if (!grupo) return null;
  if (aba === 'conferir') {
    return `Há mais de ${teto} pedidos em conferência. A Central mostra os ${teto} mais antigos; os mais recentes ficam fora da lista e da contagem até que os antigos sejam conferidos.`;
  }
  if (aba === 'pagos') {
    return `Há mais de ${teto} pagamentos neste mês. A aba mostra os ${teto} mais recentes; os pagos mais antigos do mês ficam fora da lista e da contagem.`;
  }
  return `Há mais de ${teto} pedidos aprovados ainda não pagos. Esta aba e as contagens de Aguardando ERP, Pagar e Com problema cobrem os ${teto} mais antigos; os mais recentes só aparecem depois que os antigos forem pagos ou resolvidos.`;
}

/**
 * Link direto `?aba=conferir&pedido=ID`: só a PRIMEIRA carga da aba Conferir pede ao servidor
 * a página que contém o pedido (ele pode estar fora da 1ª página). Depois disso a lista é a
 * que o usuário navega — Anterior/Próximo seguem a lista carregada.
 */
export function pedidoAlvoDaCarga(
  aba: AbaCentral, pedidoAberto: number | null, jaCarregouAlgumaVez: boolean,
): number | undefined {
  return aba === 'conferir' && pedidoAberto !== null && !jaCarregouAlgumaVez ? pedidoAberto : undefined;
}

export { formatBRL };

/** Linha recolhida do histórico na tela dividida (B2). Rótulos: `rotuloAcaoHistorico` (fonte única). */
export function resumoHistorico(e: { acao: string; ator_nome: string; created_at: string; detalhes?: Record<string, unknown> | null } | null | undefined): string {
  if (!e) return '';
  const quando = new Date(e.created_at).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo',
  });
  const rotulo = rotuloAcaoHistorico(e.acao, e.detalhes);
  return [rotulo, e.ator_nome, quando].filter(Boolean).join(' · ');
}

/** Iniciais do avatar do solicitante ("Guilherme Pessoa" → "GP"). */
export function iniciais(nome: string | null | undefined): string {
  const partes = (nome ?? '').trim().split(/\s+/).filter(Boolean);
  if (!partes.length) return '?';
  return ((partes[0][0] ?? '') + (partes.length > 1 ? partes[partes.length - 1][0] : '')).toUpperCase();
}
